import type { BlogHeroProps } from '@/root/../../packages/ui-lib/src';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { createContext } from 'use-context-selector';
import { storeLocale } from '@/root/constants';
import { useGetNailArtLandingQuery } from 'lib/contentful/__generated__/NailArtLandingQuery';
import type { GetNailArtLandingQuery } from 'lib/contentful/__generated__/NailArtLandingQuery';
import { contentfulQueryDataSource } from 'lib/contentful/dataSources';
import { formatContentfulSections } from 'utils/format/contentful/formatContentfulSections';
import { formatModuleBlogHero } from 'utils/format/contentful/formatModuleBlogHero';
import { usePreviewCookie } from 'utils/getPreviewCookie';
import type {
  FormattedContentfulSection,
  InputContentfulSections,
} from 'utils/types/contentfulSections';
import type { StoreSeo } from 'utils/types/storeTypes';

type NailArtLandingData = {
  hero?: BlogHeroProps;
  title: string;
  description?: string;
  /* Sections that display below the main Nail Art Grid */
  sections?: FormattedContentfulSection[];
  /* Sections that display on the Nail Art Landing before filters are applied */
  landingSections?: FormattedContentfulSection[];
  seo?: StoreSeo;
};

export type NailArtLandingContext = NailArtLandingData & {
  isLoading: boolean;
};

export const NailArtLandingContext = createContext<
  NailArtLandingContext | undefined
>(undefined);

export function NailArtLandingProvider({ children }: { children: ReactNode }) {
  const { locale } = useRouter();

  const preview = usePreviewCookie();

  const variables = {
    locale: storeLocale(locale),
    preview,
  };

  const { data, isLoading } = useQuery(
    useGetNailArtLandingQuery.getKey(variables),
    useGetNailArtLandingQuery.fetcher(
      contentfulQueryDataSource({ byPassCache: preview }),
      variables
    ),
    {
      select: getNailArtLandingSelect,
    }
  );

  const contextValue = useMemo(() => {
    if (data) {
      return {
        ...data,
        isLoading,
      };
    }

    return {
      isLoading,
      title: '',
    };
  }, [data, isLoading]);

  return (
    <NailArtLandingContext.Provider value={contextValue}>
      {children}
    </NailArtLandingContext.Provider>
  );
}

const getNailArtLandingSelect = (
  originalData: GetNailArtLandingQuery
): NailArtLandingData | null => {
  if (!originalData.nailArtLanding) {
    return null;
  }

  const nailArtLanding = originalData.nailArtLanding;

  const sectionsData =
    (nailArtLanding.sections?.items as InputContentfulSections) ||
    ([] as InputContentfulSections);
  const sections = formatContentfulSections(sectionsData);

  const landingSectionsData =
    (nailArtLanding.landingSections?.items as InputContentfulSections) ||
    ([] as InputContentfulSections);
  const landingSections = formatContentfulSections(landingSectionsData);

  const hero = nailArtLanding.hero
    ? formatModuleBlogHero(nailArtLanding.hero)
    : undefined;

  const seo = {
    title: originalData.nailArtLanding.seoTitle || '',
    description: originalData.nailArtLanding.seoDescription || '',
  };

  return {
    title: nailArtLanding.title ?? '',
    description: nailArtLanding.description ?? '',
    sections,
    landingSections,
    hero,
    seo,
  };
};
